import React, { useEffect, useState } from "react";
import { Card, Button, List, Modal, Form, Input, Image } from "antd";
import { useHistory, useParams } from "react-router-dom";
import { DownloadOutlined, LeftOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import { axiosInstance, BASE_API_CONFIG } from "../../util/Api";
import { isArrayNonEmpty, isValidElement, isValidString } from "../../util/ApiHelper";
import { fetchStart, fetchSuccess } from "../../appRedux/actions";
import { formatDate, showAlert } from "../../util/helper";
import { RFQStatus } from "../../util/Constant";
import { MY_EXIM_API_RFQ } from "../../appRedux/services/requestQuotationsNetwork";
import RFQStatusTags from "../../routes/requestQuotation/RFQStatus";


const AdminQuotationDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const { isMobile } = useGetBreakpoints();
  const dispatch = useDispatch();
  const authUser = useSelector(({ auth }) => auth.userData);
  const [fileName, setFileName] = useState("");
  const [quotationsData, setQuotationsData] = useState(null);
  const [messageSubmit, setMessageSubmit] = useState(false);

  useEffect(
    () => {
      if (isArrayNonEmpty(quotationsData?.attachments)) {
        setFileName(quotationsData?.attachments);
      }
      if (messageSubmit) {
        setMessageSubmit(false);
      }
    },
    [quotationsData],
    [messageSubmit]
  );

  useEffect(() => {
    window.scrollTo({ top: -100, left: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const fetchQuotationDetails = async () => {
      if (isValidElement(id)) {
        try {
          dispatch(fetchStart());
          const response = await axiosInstance.get(
            `${BASE_API_CONFIG.baseURL}/admin/quotations/${id}`
          );

          let dataItem = response?.data?.data;
          if (dataItem) {
            setQuotationsData(dataItem);
          }

          dispatch(fetchSuccess());
        } catch (error) {
          if (isValidString(error?.message)) {
            showAlert(error?.message);
          } else {
            showAlert("Something went wrong while fetching quotation details.");
          }
          dispatch(fetchSuccess());
        }
      }
    };

    fetchQuotationDetails();
  }, [id, dispatch]);

  const approveQuotation = () => {
    let params = {
      id: id,
      data: {
        ...quotationsData,
        status: RFQStatus.ORDER_PLACED,
      },
    };
    dispatch(fetchStart());
    MY_EXIM_API_RFQ.updateQuotationDetails(params)
      .then((response) => {
        if (isValidElement(response)) {
          history.goBack();
        }
        dispatch(fetchSuccess());
      })
      .catch((e) => {
        showAlert(e?.message);
        dispatch(fetchSuccess());
      });
  };

  const {
    sender,
    price,
    units,
    description,
    enquiries,
    createdAt,
    currencyCode,
    status,
    paymentTerms,
  } = quotationsData ?? {};

  const { shipmentMode, shipmentType, containerType } =
    quotationsData?.enquiries ?? {};

  const unitsDisplay =
    shipmentMode === "sea"
      ? shipmentType === "fcl"
        ? `Unit (${containerType || "N/A"})`
        : "Cubic Meter"
      : units;

  const { companyName, country } = sender ?? {};
  let data = [
    {
      label: "Received Date",
      value: formatDate(createdAt),
    },
     isValidString(country) &&{
      label: "Source Port",
      value: enquiries.sourcePort,
    },
    isValidString(country) && {
      label: "Destination Country",
      value: enquiries.destinationCountry,
    },
    isValidString(country) && {
      label: "Destination Port",
      value: enquiries.destinationPort,
    },
    isValidString(paymentTerms) && {
      label: "Delivery Terms",
      value: paymentTerms,
    },
    isValidString(currencyCode) &&
      isValidString(price) &&
      (shipmentMode && shipmentType
        ? isValidString(containerType)
        : isValidString(units)) && {
        label: "Price / Unit",
        value: `${currencyCode} ${price} / ${unitsDisplay}`,
      },
    isValidString(enquiries?.qty) && {
      label: "Total Quantity",
      value: `${enquiries?.qty} ${units}`,
    },
    {
      label: "Response Message",
      value: description,
    },
    {
      label: "Status",
      value: <RFQStatusTags status={status} />,
    },
  ];

  if (isValidElement(companyName)) {
    return (
      <>
        <Card className="myexim-detail-card">
          <div className="myexim-table-title">
            <LeftOutlined
              className={"back-icon"}
              size="large"
              onClick={() => {
                history.goBack();
              }}
            />
            Quotation Details
          </div>
          <div className="gx-pl-4">
            <List
              className="gx-mt-4"
              itemLayout="horizontal"
              dataSource={data}
              renderItem={(item) =>
                item?.value && (
                  <List.Item className="myexim-details-list">
                    <List.Item.Meta
                      title={<div>{item.label}</div>}
                      description={item.value}
                    />
                  </List.Item>
                )
              }
            />
          </div>
          {fileName?.length > 0 && (
            <div className="attachments gx-pl-2">
              <div className="sub-header">Attachments</div>
              <div className="gx-pt-4">
                {fileName?.map((file, index) => (
                  <div key={index} className="gx-pb-3">
                    <Image
                      width={150}
                      height={150}
                      src={file?.src}
                      alt={file?.originalName}
                    />
                    <div className="gx-pt-2">
                      <a
                        href={file?.downloadUrl || file?.src}
                        download={file?.originalName}
                        className="download-link"
                        style={{
                          color: "#1677ff",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <DownloadOutlined style={{ fontSize: "18px" }} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </>
    );
  } else return null;
};

export default AdminQuotationDetails;
